import { type SanityFrameBuilderGlass } from '@frame-builder/data/sanity/queries/types/glass'
import { type SanityFrameBuilderPassepartoutColor } from '@frame-builder/data/sanity/queries/types/passepartout'
import { SanityFrameBuilderProfile } from '@frame-builder/data/sanity/queries/types/profile'
import {
  type SanityFrameBuilderSpacerColor,
  type SanityFrameBuilderSpacerSize,
} from '@frame-builder/data/sanity/queries/types/spacer'
import { type SanityFrameBuilderTexture } from '@frame-builder/data/sanity/queries/types/texture'
import { type FrameBuilderState } from './types'

interface UpdateArtworkSizeAction {
  type: 'artworkSize'
  width?: number
  height?: number
}

interface UpdateArtworkImageAction {
  type: 'artworkImage'
  url?: string
  name?: string
  color?: string
}

interface UpdateFrameStyleAction {
  type: 'frameStyle'
  profile?: SanityFrameBuilderProfile
  texture?: SanityFrameBuilderTexture
}

interface UpdatePassepartoutAction {
  type: 'passepartout'
  color?: SanityFrameBuilderPassepartoutColor
  width?: number
  height?: number
}

interface UpdateGlassAction {
  type: 'glass'
  glass?: SanityFrameBuilderGlass
}

interface UpdateSpacerAction {
  type: 'spacer'
  size?: SanityFrameBuilderSpacerSize
  color?: SanityFrameBuilderSpacerColor
}

export type FrameBuilderStateAction =
  | UpdateArtworkSizeAction
  | UpdateArtworkImageAction
  | UpdateFrameStyleAction
  | UpdatePassepartoutAction
  | UpdateGlassAction
  | UpdateSpacerAction

/**
 * Updates artwork size state.
 */
const updateArtworkSize = (
  state: FrameBuilderState,
  action: UpdateArtworkSizeAction,
) => {
  const partialState: Pick<FrameBuilderState, 'artwork'> = {
    artwork: {
      ...state.artwork,
    },
  }

  if ('width' in action && typeof action.width !== 'undefined') {
    partialState.artwork.width = action.width
  }

  if ('height' in action && typeof action.height !== 'undefined') {
    partialState.artwork.height = action.height
  }

  return partialState
}

/**
 * Updates artwork image state.
 */
const updateArtworkImage = (
  state: FrameBuilderState,
  action: UpdateArtworkImageAction,
) => {
  const partialState: Pick<FrameBuilderState, 'artwork'> = {
    artwork: {
      ...state.artwork,
    },
  }

  if (
    'url' in action &&
    'name' in action &&
    typeof action.url !== 'undefined' &&
    typeof action.name !== 'undefined'
  ) {
    partialState.artwork.image = {
      url: action.url,
      name: action.name,
    }
  }

  if ('color' in action) {
    partialState.artwork.backgroundColor = {
      ...partialState.artwork.backgroundColor,
      current: action.color,
    }
  }

  return partialState
}

/**
 * Updates frame style state.
 */
const updateFrameStyle = (
  state: FrameBuilderState,
  action: UpdateFrameStyleAction,
) => {
  const partialState: Pick<FrameBuilderState, 'profile' | 'texture'> = {}

  if (typeof state.profile !== 'undefined') {
    partialState.profile = state.profile
  }

  if (typeof state.texture !== 'undefined') {
    partialState.texture = state.texture
  }

  if ('profile' in action) {
    partialState.profile = action.profile
  }

  if ('texture' in action) {
    partialState.texture = action.texture
  }

  return partialState
}

/**
 * Updates mount state.
 */
const updatePassepartout = (
  state: FrameBuilderState,
  action: UpdatePassepartoutAction,
) => {
  const partialState: Pick<FrameBuilderState, 'passepartout'> = {
    passepartout: {},
  }

  if (typeof state.passepartout.color !== 'undefined') {
    partialState.passepartout.color = state.passepartout.color
  }

  if (typeof state.passepartout.width !== 'undefined') {
    partialState.passepartout.width = state.passepartout.width
  }

  if (typeof state.passepartout.height !== 'undefined') {
    partialState.passepartout.height = state.passepartout.height
  }

  if ('color' in action) {
    partialState.passepartout.color = action.color
  }

  if ('width' in action) {
    partialState.passepartout.width = action.width
  }

  if ('height' in action) {
    partialState.passepartout.height = action.height
  }

  return partialState
}

/**
 * Updates glass state.
 */
const updateGlass = (state: FrameBuilderState, action: UpdateGlassAction) => {
  const partialState: Pick<FrameBuilderState, 'glass'> = {}

  if (typeof state.glass !== 'undefined') {
    partialState.glass = state.glass
  }

  if ('glass' in action) {
    partialState.glass = action.glass
  }

  return partialState
}

/**
 * Updates spacer state.
 */
const updateSpacer = (state: FrameBuilderState, action: UpdateSpacerAction) => {
  const partialState: Pick<FrameBuilderState, 'spacerSize' | 'spacerColor'> = {}

  if (typeof state.spacerSize !== 'undefined') {
    partialState.spacerSize = state.spacerSize
  }

  if (typeof state.spacerColor !== 'undefined') {
    partialState.spacerColor = state.spacerColor
  }

  if ('size' in action) {
    partialState.spacerSize = action.size
  }

  if ('color' in action) {
    partialState.spacerColor = action.color
  }

  return partialState
}

/**
 * Updates frame builder state.
 */
export const frameBuilderStateReducer = (
  state: FrameBuilderState,
  action: FrameBuilderStateAction,
): FrameBuilderState => {
  switch (action.type) {
    case 'artworkSize': {
      const partialState = updateArtworkSize(state, action)

      return {
        ...state,
        ...partialState,
      }
    }

    case 'artworkImage': {
      const partialState = updateArtworkImage(state, action)

      return {
        ...state,
        ...partialState,
      }
    }

    case 'frameStyle': {
      const partialState = updateFrameStyle(state, action)

      return {
        ...state,
        ...partialState,
      }
    }

    case 'passepartout': {
      const partialState = updatePassepartout(state, action)

      return {
        ...state,
        ...partialState,
      }
    }

    case 'glass': {
      const partialState = updateGlass(state, action)

      return {
        ...state,
        ...partialState,
      }
    }

    case 'spacer': {
      const partialState = updateSpacer(state, action)

      return {
        ...state,
        ...partialState,
      }
    }
  }
}
